import moment from 'moment'
import { countBy } from '@fxts/core'
/**
 * 주어진 날짜의 연도를 반환합니다.
 * 날짜가 유효하지 않은 경우 현재 연도를 반환합니다.
 *
 * @param {Date | string | moment} [date=new Date() | string | moment] - 연도를 가져올 연월일, date 객체, moment 객체
 * @returns {number} - 연도 (YYYY 형식)
 */
export const getYear = (date = new Date()) => {
  const momoentDate = moment(date)

  if (momoentDate.isValid()) {
    return momoentDate.year()
  }

  return moment().year()
}

/**
 * 주어진 날짜의 월을 반환합니다.
 * 날짜가 유효하지 않은 경우 현재 월을 반환합니다.
 *
 * @param {Date | string | moment} [date=new Date() | string | moment] - 월을 가져올 연월일, date 객체, moment 객체
 * @returns {number} - 월 (1-12)
 */
export const getMonth = (date = new Date()) => {
  const momoentDate = moment(date)

  if (momoentDate.isValid()) {
    return momoentDate.month() + 1
  }

  return moment().month() + 1
}

/**
 * 주어진 날짜가 속한 달의 주 수를 반환합니다.
 *
 * @param {Date | string | moment} [date=new Date() | string | moment] - 월을 가져올 연월일, date 객체, moment 객체
 * @returns {number} - 주 수
 */
const countWeeksInMonth = (date = new Date()) => {
  const momentDate = moment(date)

  if (!momentDate.isValid()) {
    throw new Error('Invalid date')
  }

  const firstOfMonth = new Date(momentDate.year(), momentDate.month(), 1)
  const lastOfMonth = new Date(momentDate.year(), momentDate.month() + 1, 0)

  // (이달의 시작하는 인덱스 + 이달의 마지막 날짜) / 7 + 소수점 올림 = 이달의 주 수
  const currentMonthOfWeeks = firstOfMonth.getDay() + lastOfMonth.getDate()

  return Math.ceil(currentMonthOfWeeks / 7)
}

/**
 * 달력 배열을 초기화합니다.
 *
 * @param {Date | string | moment} [date=new Date() | string | moment] - 월을 가져올 연월일, date 객체, moment 객체
 * @returns {Array<{disabled: true, day : 0}>} - 0으로 초기화 된 달력 배열
 */
const initializeCalendarArray = (date) =>
  Array(countWeeksInMonth(date))
    .fill(0)
    .map(() => [
      { disabled: true, day: 0 },
      { disabled: true, day: 0 },
      { disabled: true, day: 0 },
      { disabled: true, day: 0 },
      { disabled: true, day: 0 },
      { disabled: true, day: 0 },
      { disabled: true, day: 0 },
    ])

/**
 * 주어진 날짜가 속한 달의 달력 객체형식으로 반환 합니다.
 *
 * @param {Date | string | moment} [date=new Date() | string | moment] - 월을 가져올 연월일, date 객체, moment 객체
 * @returns {Array<{disabled: boolean, day : number}>} - 이 달의 달력 배열
 */
export const getDayOfWeekByDate = (date = new Date()) => {
  const FIRST_WEEK_INDEX = 0
  const INITIALIZED_DAY = 0
  const SUNDAY_INDEX = 0

  const calendarArray = initializeCalendarArray(date)
  const startDate = moment(date)
  const dayInMonth = startDate.daysInMonth()

  let dayCount = 0
  let dayIndex = moment(startDate).startOf('month').day()

  for (let weekIndex = SUNDAY_INDEX; weekIndex < calendarArray.length; weekIndex++) {
    // week
    if (weekIndex > SUNDAY_INDEX) dayIndex = SUNDAY_INDEX // calendarArray의 2주차 부터 적용할 day index를 사용하지 않음

    for (; dayIndex < 7; dayIndex++) {
      // day
      const day = moment(startDate).startOf('month').add(dayCount++, 'day')
      const dayObj = {
        disabled: getMonth(day) !== getMonth(startDate),
        day: day.date(),
      }

      if (dayInMonth === day) dayCount = 0

      calendarArray[weekIndex][dayIndex] = dayObj
    }
  }

  const previousDiffDateCount = countBy(
    (date) => date.day === INITIALIZED_DAY,
    calendarArray[FIRST_WEEK_INDEX]
  )['true']
  let previousDateBySubstractDateCount = moment(startDate)
    .startOf('month')
    .subtract(previousDiffDateCount, 'day')
    .date()

  for (
    let dayIndex = 0;
    dayIndex < previousDiffDateCount;
    previousDateBySubstractDateCount++, dayIndex++
  ) {
    calendarArray[FIRST_WEEK_INDEX][dayIndex] = {
      disabled: true,
      day: previousDateBySubstractDateCount,
    }
  }

  return calendarArray
}
